import React, { Fragment, useContext, useRef } from 'react';
import { DecisionTreeContext } from '../context/decision-tree-context';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Button,
  Link
} from '@chakra-ui/react';
import classNames from '../../../../utils/classnames';
import { SharedIconArrowDown, SharedIconCheck, SharedIconStripe } from '../../../shared/icons';
import { DecisionTreeResultFooter } from './decision-tree-result-footer';
import useMediaQuery from '../../../../hooks/use-media';
import { mainMediaMobile, scrollTo } from '../../../../utils';
import { DecisionTreeQuestion } from '../../../../@types';

export const DecisionTreeResultAccordion: React.FC = () => {
  const { questions, appType, resetToQuestion } = useContext(DecisionTreeContext);

  const isMobile = useMediaQuery(mainMediaMobile);
  // Create an array of refs
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const clearAnswers = () => {
    resetToQuestion(questions[0]);
    scrollTo('.react-tools');
  };
  const scrollToItem = (index: number, isExpanded: boolean) => {
    if (isExpanded && !isMobile) {
      return;
    }

    const itemRef = itemRefs.current[index];
    if (itemRef) {
      setTimeout(() => {
        itemRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const getExplanation = (question: DecisionTreeQuestion) => {
    const explanation = question.answer?.explanation;
    const conditionals = explanation?.conditionals ? explanation.conditionals : null;
    if (conditionals) {
      const expl = conditionals.find((conditional) => {
        const q = questions.find((question) => question.id === conditional.questionId);
        if (q?.answer) {
          if (q.answer.id === conditional.hasAnswerId) {
            return conditional;
          }
        }
      });

      if (expl) {
        //console.log('expl condition met', expl)
        return expl.explanation;
      } else {
        //console.log('expl condition not met', question.answer?.explanation.value)
        return question.answer?.explanation.value;
      }
    } else {
      //console.log('no conditions', question.answer?.explanation.value)
      return question.answer?.explanation.value;
    }
  };

  const accordionButtonClasses = classNames({
    'cursor-pointer': true
  });

  return (
    <>
      <Accordion allowMultiple={true} defaultIndex={[0]}>
        {questions
          .filter((q) => !q.hide)
          .map((q, key) => (
            <AccordionItem key={key}>
              {({ isExpanded }) => (
                <div
                  className={`rounded shadow shadow-shadow-500 bg-white mb-4 px-[1rem]`}
                  ref={(el) => (itemRefs.current[key] = el)}>
                  <Fragment>
                    <AccordionButton
                      p={0}
                      width={'100%'}
                      className={accordionButtonClasses}
                      onClick={() => scrollToItem(key, isExpanded)}>
                      <div
                        className={`flex justify-between w-full items-center min-h-[60px] md:px-4`}>
                        <div className={`flex flex-grow items-center`}>
                          <SharedIconCheck mr={`2`} color={`green.500`}></SharedIconCheck>
                          {q.type}
                        </div>
                        <div className={`mr-4 text-gray-500`}>
                          {q.answer?.shortAnswer || q.answer?.answer}
                        </div>
                        <div
                          className={`rounded-full w-[3.2rem] h-[3.2rem] items-center flex justify-center ${
                            isExpanded ? 'bg-blue-500' : 'bg-gray-300'
                          }`}>
                          {isExpanded ? (
                            <SharedIconStripe color={`white`} boxSize={6} />
                          ) : (
                            <SharedIconArrowDown color={`bg-blue-500`} boxSize={6} />
                          )}
                        </div>
                      </div>
                    </AccordionButton>
                    <AccordionPanel p={0}>
                      <div className={`md:px-6 pb-6`}>
                        <div
                          className={`mb-4`}
                          dangerouslySetInnerHTML={{ __html: getExplanation(q) || '' }}></div>
                        <Link onClick={() => resetToQuestion(q)}>Wijzig antwoord</Link>
                      </div>
                    </AccordionPanel>
                  </Fragment>
                </div>
              )}
            </AccordionItem>
          ))}
      </Accordion>
      <div className={`flex justify-between items-center my-8`}>
        <p className={`text-gray-400`}>Vragen opnieuw invullen?</p>
        <Button variant={`outline`} colorScheme={`blue`} onClick={() => clearAnswers()}>
          Alle antwoorden wissen
        </Button>
      </div>
      {appType === 'remodel' && <DecisionTreeResultFooter></DecisionTreeResultFooter>}
    </>
  );
};
